import React, { useState } from 'react';
import { NavLink, useLocation, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../utils/AuthContext';
import {
    LayoutDashboard,
    User,
    Users,
    Package,
    FileText,
    Receipt,
    Menu,
    X,
    Calculator,
    ChevronLeft,
    ChevronRight,
    Database,
    HelpCircle,
    Layers
} from 'lucide-react';
import UserMenu from './UserMenu';

const Layout = ({ children }) => {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [openDropdown, setOpenDropdown] = useState(null);
    const location = useLocation();

    const navigation = [
        {
            name: 'Dashboard',
            href: '/dashboard',
            icon: LayoutDashboard,
            description: 'Overview with stats (invoices generated, customers, earnings)'
        },
        {
            name: 'Rate Master',
            href: '/rates',
            icon: Calculator,
            description: 'Manage freight rates and zones'
        },
        {
            name: 'Customer Management',
            href: '/customers',
            icon: Users,
            description: 'Add/Edit/Delete customers (ID, name, GST, address, etc.)'
        },
        {
            name: 'Booking',
            href: '/booking',
            icon: Package,
            description: 'Add/modify bookings',
            children: [
                { name: 'Add', href: '/booking' },
                { name: 'Multiple Booking', href: '/booking/multiple' },
                { name: 'View Bookings', href: '/booking/multiple/view' },
                { name: 'Modify', href: '/booking/modify' },
                { name: 'Bulk Modify', href: '/booking/bulk-modify' }
            ]
        },
        {
            name: 'Invoice',
            href: '/invoice',
            icon: FileText,
            description: 'Generate and manage invoices',
            children: [
                { name: 'Generate GST Invoice', href: '/invoice-generator' },
                { name: 'Generate Non-GST Invoice', href: '/invoice-generator-non-gst' },
                { name: 'View Invoices', href: '/invoices' },
            ]
        },
        {
            name: 'Payment',
            href: '/payments',
            icon: Receipt,
            description: 'Record payments and track balances',
            children: [
                { name: 'Add Payments', href: '/payments' },
                { name: 'Payment Details', href: '/payment-details' },
                { name: 'Expense Tracker', href: '/payment/expenses' },
                { name: 'Creditor\'s Report', href: '/creditors-report' }
            ]
        },
        {
            name: 'Data Management',
            href: '/data-management',
            icon: Database,
            description: 'Export and manage your franchise operations data'
        },
        {
            name: 'Customer Support',
            href: '/support',
            icon: HelpCircle,
            description: 'Direct support channel for franchise technical and billing operations'
        },
        /*
        {
            name: 'Stationary Management',
            href: '/stationary',
            icon: Layers,
            description: 'Manage stationary purchases and inventory',
            children: [
                { name: 'Purchase', href: '/stationary/purchase' },
                { name: 'Sales', href: '/stationary/sales' },
                { name: 'View Inventory', href: '/stationary/view' }
            ]
        },
        */
    ];

    const navigate = useNavigate();
    const { user, signOut } = useAuth();

    const handleLogout = async () => {
        try {
            await signOut();
            navigate('/login', { replace: true });
        } catch (e) {
            // noop
        }
    };

    return (
        <div className="flex flex-col h-screen bg-yellow-50 print:bg-white print:h-auto font-sans">
            {/* Top Header Bar - Full Width, Fixed */}
            <div className="bg-white/80 backdrop-blur-lg border-b border-slate-200/50 h-16 flex-none z-30 relative print:hidden shadow-sm transition-all">
                {/* Indigo Accent Line */}
                <div className="absolute top-0 left-0 w-full h-[3px] bg-indigo-600"></div>

                <div className="flex items-center justify-between h-full px-4 sm:px-6 lg:px-8 pt-[3px]">
                    <div className="flex items-center">
                        <button
                            onClick={() => setSidebarOpen(true)}
                            className="lg:hidden p-2 rounded-md text-indigo-900 hover:text-orange-500 mr-4 transition-colors"
                        >
                            <Menu size={24} />
                        </button>

                        {/* Typographical Brand Logo Sync */}
                        <div className="flex items-center cursor-pointer select-none group" onClick={() => navigate('/dashboard')}>
                            <span className="text-2xl font-black tracking-tight text-indigo-900 transition-colors group-hover:text-indigo-700">FR-Bill</span>
                            <span className="text-2xl font-black tracking-tight text-orange-500 transition-colors group-hover:text-orange-600">Net</span>
                        </div>
                    </div>
                    <div className="flex items-center space-x-4">
                        <UserMenu user={user} onLogout={handleLogout} />
                    </div>
                </div>
            </div>

            {/* Main Layout Body (Sidebar + Content) */}
            <div className="flex flex-1 overflow-hidden relative print:overflow-visible">

                {/* Mobile sidebar overlay */}
                {sidebarOpen && (
                    <div
                        className="fixed inset-0 z-40 bg-indigo-950/80 backdrop-blur-sm lg:hidden print:hidden"
                        onClick={() => setSidebarOpen(false)}
                    />
                )}

                {/* Sidebar - Deep Navy Theme */}
                <div className={`
          absolute inset-y-0 left-0 z-50 bg-indigo-950 shadow-2xl transform transition-all duration-300 ease-in-out lg:static lg:translate-x-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          ${isCollapsed ? 'w-20' : 'w-64'}
          border-r border-indigo-900 print:hidden
        `}>
                    {/* Sidebar Header (Collapse Toggle) */}
                    <div className={`flex items-center h-12 px-4 border-b border-indigo-900/50 ${isCollapsed ? 'justify-center' : 'justify-end'}`}>
                        <button
                            onClick={() => setSidebarOpen(false)}
                            className="lg:hidden p-2 rounded-md text-indigo-400 hover:text-white transition-colors"
                        >
                            <X size={20} />
                        </button>

                        {/* Desktop Collapse Toggle */}
                        <button
                            onClick={() => setIsCollapsed(!isCollapsed)}
                            className="hidden lg:block p-1.5 rounded-md text-indigo-400 hover:text-white hover:bg-indigo-900 transition-colors"
                        >
                            {isCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
                        </button>
                    </div>

                    <nav className="mt-4 px-3 overflow-y-auto h-[calc(100%-3rem)] custom-scrollbar">
                        <div className="space-y-1">
                            {navigation.map((item) => {
                                const isActive = location.pathname === item.href;
                                if (item.children) {
                                    const isParentActive = location.pathname.startsWith(item.href) ||
                                        (item.children || []).some(child => location.pathname === child.href);
                                    const isOpen = openDropdown === item.name || isParentActive;

                                    return (
                                        <div key={item.name} className="space-y-1">
                                            <button
                                                className={`
                                                  w-full flex items-center px-3 py-2.5 text-sm font-semibold rounded-md transition-colors duration-200
                                                  ${isParentActive
                                                        ? 'bg-orange-500/10 text-white border-l-4 border-orange-500 shadow-inner'
                                                        : 'text-indigo-200 hover:bg-indigo-900/50 hover:text-white border-l-4 border-transparent'
                                                    }
                                                  ${isCollapsed ? 'justify-center border-l-0 border-b-4' : 'justify-between'}
                                                `}
                                                onClick={() => {
                                                    if (isCollapsed) {
                                                        setIsCollapsed(false);
                                                        setOpenDropdown(item.name);
                                                    } else {
                                                        setOpenDropdown(prev => prev === item.name ? null : item.name);
                                                    }
                                                }}
                                                title={isCollapsed ? item.name : ''}
                                            >
                                                <span className="flex items-center">
                                                    <item.icon
                                                        className={`
                                                          h-5 w-5 flex-shrink-0
                                                          ${!isCollapsed && 'mr-3'}
                                                          ${isParentActive ? 'text-orange-400' : 'text-indigo-400 group-hover:text-indigo-200'}
                                                        `}
                                                    />
                                                    {!isCollapsed && item.name}
                                                </span>
                                                {!isCollapsed && (
                                                    <svg className={`h-4 w-4 transition-transform ${isOpen ? 'transform rotate-90 text-white' : 'text-indigo-400'}`} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                                        <path fillRule="evenodd" d="M6 6a1 1 0 011.707-.707l4 4a1 1 0 010 1.414l-4 4A1 1 0 016 13.586L9.586 10 6.707 7.121A1 1 0 016 6z" clipRule="evenodd" />
                                                    </svg>
                                                )}
                                            </button>
                                            {isOpen && !isCollapsed && (
                                                <div className="ml-9 space-y-1 mt-1 border-l border-indigo-800/50 pl-2">
                                                    {item.children.map((child) => (
                                                        <NavLink
                                                            key={child.name}
                                                            to={child.href}
                                                            className={`
                                                              block px-3 py-2 text-sm rounded-md transition-colors font-medium
                                                              ${location.pathname === child.href ? 'text-orange-400 font-bold bg-white/5' : 'text-indigo-300 hover:bg-white/5 hover:text-white'}
                                                            `}
                                                            onClick={() => setSidebarOpen(false)}
                                                        >
                                                            {child.name}
                                                        </NavLink>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    );
                                }
                                return (
                                    <NavLink
                                        key={item.name}
                                        to={item.href}
                                        className={`
                                          group flex items-center px-3 py-2.5 text-sm font-semibold rounded-md transition-colors duration-200
                                          ${isActive
                                                ? 'bg-orange-500/10 text-white border-l-4 border-orange-500 shadow-inner'
                                                : 'text-indigo-200 hover:bg-indigo-900/50 hover:text-white border-l-4 border-transparent'
                                            }
                                          ${isCollapsed ? 'justify-center border-l-0 border-b-4' : ''}
                                        `}
                                        onClick={() => setSidebarOpen(false)}
                                        title={isCollapsed ? item.name : ''}
                                    >
                                        <item.icon
                                            className={`
                                              h-5 w-5 flex-shrink-0
                                              ${!isCollapsed && 'mr-3'}
                                              ${isActive ? 'text-orange-400' : 'text-indigo-400 group-hover:text-indigo-200'}
                                            `}
                                        />
                                        {!isCollapsed && item.name}
                                    </NavLink>
                                );
                            })}
                        </div>
                    </nav>
                </div>

                {/* Page content */}
                <main className="flex-1 overflow-y-auto bg-yellow-50 print:bg-white print:overflow-visible">
                    <div className="min-h-full p-4 sm:p-6 lg:p-8">
                        <Outlet />

                        {/* Footer */}
                        {!(location.pathname.startsWith('/invoice-generator') || location.pathname.startsWith('/invoices')) && (
                            <footer className="mt-12 border-t border-slate-200">
                                <div className="py-6 text-center text-xs font-semibold text-slate-400 uppercase tracking-widest">
                                    Proprietary Billing Infrastructure
                                </div>
                            </footer>
                        )}
                    </div>
                </main>
            </div>
        </div>
    );
};

export default Layout;
